import React, { useState, useEffect, useMemo } from 'react';
import api from '../../api/client';
import toast from 'react-hot-toast';
import { Users, Shield, ShieldCheck, ChevronDown, UserX, UserCheck, Search, ChevronLeft, ChevronRight, Filter, Mail, UserPlus } from 'lucide-react';
import AdminPageHeader from '../../components/admin/AdminPageHeader';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';

const PAGE_SIZE = 15;

export default function UserManager() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleDropdown, setRoleDropdown] = useState(null);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('active');
  const [filterAdmin, setFilterAdmin] = useState('all');
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRoleId, setInviteRoleId] = useState('');
  const [inviting, setInviting] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [showInvitations, setShowInvitations] = useState(false);
  const { user: currentUser } = useAuthStore();
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const [usersRes, rolesRes, invitesRes] = await Promise.all([
        api.get('/users'),
        api.get('/roles'),
        api.get('/admin/invitations'),
      ]);
      setUsers(usersRes.data.users || []);
      setRoles(rolesRes.data.roles || []);
      setInvitations(invitesRes.data.invitations || []);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      await api.post('/auth/invite', { email: inviteEmail.trim(), role_id: inviteRoleId || null });
      toast.success(`Invite sent to ${inviteEmail}`);
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRoleId('');
      fetchData();
    } catch(err) {
      toast.error(err.response?.data?.error || 'Failed to send invite');
    } finally { setInviting(false); }
  };

  const handleRevokeInvite = async (id) => {
    try {
      await api.delete(`/admin/invitations/${id}`);
      toast.success('Invite revoked');
      fetchData();
    } catch(err) { toast.error('Failed to revoke invite'); }
  };

  const handleResendInvite = async (id) => {
    try {
      await api.post(`/admin/invitations/${id}/resend`);
      toast.success('Invite resent');
      fetchData();
    } catch(err) { toast.error('Failed to resend invite'); }
  };

  const filtered = useMemo(() => {
    let result = users;
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(u =>
        `${u.first_name} ${u.last_name}`.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        (u.role_name || '').toLowerCase().includes(q)
      );
    }
    if (filterStatus === 'active') result = result.filter(u => u.is_active);
    else if (filterStatus === 'inactive') result = result.filter(u => !u.is_active);
    if (filterRole !== 'all') {
      if (filterRole === 'none') result = result.filter(u => !u.role_id);
      else result = result.filter(u => u.role_id === filterRole);
    }
    if (filterAdmin === 'admin') result = result.filter(u => u.is_admin);
    else if (filterAdmin === 'member') result = result.filter(u => !u.is_admin);
    return result;
  }, [users, search, filterRole, filterStatus, filterAdmin]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => { setPage(1); }, [search, filterRole, filterStatus, filterAdmin]);

  const handleRoleChange = async (userId, roleId) => {
    try {
      await api.put(`/users/${userId}/role`, { role_id: roleId || null });
      toast.success('Role updated');
      setRoleDropdown(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update role');
    }
  };

  const handleToggleAdmin = async (user) => {
    const action = user.is_admin ? 'remove admin from' : 'make admin';
    if (!window.confirm(`${action} ${user.first_name} ${user.last_name}?`)) return;
    try {
      await api.put(`/users/${user.id}/admin`, { is_admin: !user.is_admin });
      toast.success(user.is_admin ? 'Admin removed' : 'Admin granted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update');
    }
  };

  const handleToggleActive = async (user) => {
    const action = user.is_active ? 'Deactivate' : 'Reactivate';
    if (!window.confirm(`${action} ${user.first_name} ${user.last_name}?`)) return;
    try {
      await api.put(`/users/${user.id}/status`, { is_active: !user.is_active });
      toast.success(user.is_active ? 'User deactivated' : 'User reactivated');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update');
    }
  };

  const getInitials = (u) => ((u.first_name?.[0] || '') + (u.last_name?.[0] || '')).toUpperCase();

  const formatDate = (d) => {
    if (!d) return '—';
    const now = new Date(), dt = new Date(d), diff = now - dt;
    if (diff < 86400000) return 'Today';
    if (diff < 172800000) return 'Yesterday';
    if (diff < 604800000) return Math.floor(diff / 86400000) + 'd ago';
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const activeFilters = [filterRole !== 'all', filterStatus !== 'active', filterAdmin !== 'all'].filter(Boolean).length;

  if (loading) return <div className="text-center py-12 text-gray-400">Loading users...</div>;

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <AdminPageHeader title="User Management" description={`${users.filter(u => u.is_active).length} active · ${users.length} total`} icon={Users} iconColor="text-blue-600" iconBg="bg-blue-50" />

      {/* Invite actions */}
      <div className="flex items-center gap-2 mb-3">
        <button onClick={() => setShowInvitations(!showInvitations)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
          <Mail className="h-4 w-4" />
          Invitations {invitations.filter(i => i.status === 'pending').length > 0 && (
            <span className="bg-purple-100 text-purple-700 text-xs px-1.5 py-0.5 rounded-full">
              {invitations.filter(i => i.status === 'pending').length} pending
            </span>
          )}
        </button>
        <button onClick={() => setShowInviteModal(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors">
          <UserPlus className="h-4 w-4" />
          Invite Worker
        </button>
      </div>

      {/* Invitations table */}
      {showInvitations && invitations.length > 0 && (
        <div className="mb-4 bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-4 py-2 border-b border-gray-100 bg-gray-50">
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Invitations</p>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="px-4 py-2 text-left text-[11px] text-gray-400 font-medium">Email</th>
                <th className="px-4 py-2 text-left text-[11px] text-gray-400 font-medium">Role</th>
                <th className="px-4 py-2 text-left text-[11px] text-gray-400 font-medium">Status</th>
                <th className="px-4 py-2 text-left text-[11px] text-gray-400 font-medium">Expires</th>
                <th className="px-4 py-2 text-left text-[11px] text-gray-400 font-medium">Invited By</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {invitations.map(inv => (
                <tr key={inv.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50">
                  <td className="px-4 py-2 text-[12px] text-gray-700">{inv.email}</td>
                  <td className="px-4 py-2 text-[12px] text-gray-500">{inv.role_name || '—'}</td>
                  <td className="px-4 py-2">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${
                      inv.status === 'accepted' ? 'bg-green-100 text-green-700' :
                      inv.status === 'expired' ? 'bg-gray-100 text-gray-500' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>{inv.status}</span>
                  </td>
                  <td className="px-4 py-2 text-[11px] text-gray-400">{formatDate(inv.expires_at)}</td>
                  <td className="px-4 py-2 text-[11px] text-gray-400">
                    {`${inv.invited_by_first || ''} ${inv.invited_by_last || ''}`.trim()}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {inv.status === 'pending' && (
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => handleResendInvite(inv.id)}
                          className="text-[11px] px-2 py-1 text-blue-600 hover:bg-blue-50 rounded transition-colors">
                          Resend
                        </button>
                        <button onClick={() => handleRevokeInvite(inv.id)}
                          className="text-[11px] px-2 py-1 text-red-500 hover:bg-red-50 rounded transition-colors">
                          Revoke
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Search + Filters bar */}
      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, or role..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-lg transition-colors ${showFilters || activeFilters ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
        >
          <Filter className="h-3.5 w-3.5" />
          Filters{activeFilters > 0 && <span className="text-[10px] bg-blue-600 text-white rounded-full px-1.5 py-0.5 font-bold">{activeFilters}</span>}
        </button>
      </div>

      {/* Filter row */}
      {showFilters && (
        <div className="flex items-center gap-3 mb-3 px-1">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">Status:</span>
            {['active', 'inactive', 'all'].map(s => (
              <button key={s} onClick={() => setFilterStatus(s)}
                className={`text-xs px-2 py-0.5 rounded-full border transition-colors capitalize ${filterStatus === s ? 'bg-gray-800 text-white border-gray-800' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}
              >{s}</button>
            ))}
          </div>
          <div className="w-px h-4 bg-gray-200" />
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">Role:</span>
            <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)}
              className="text-xs border border-gray-200 rounded-lg px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-400">
              <option value="all">All Roles</option>
              <option value="none">No Role</option>
              {roles.filter(r => r.is_active).map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>
          <div className="w-px h-4 bg-gray-200" />
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">Access:</span>
            {['all', 'admin', 'member'].map(s => (
              <button key={s} onClick={() => setFilterAdmin(s)}
                className={`text-xs px-2 py-0.5 rounded-full border transition-colors capitalize ${filterAdmin === s ? 'bg-gray-800 text-white border-gray-800' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}
              >{s}</button>
            ))}
          </div>
          {activeFilters > 0 && (
            <>
              <div className="w-px h-4 bg-gray-200" />
              <button onClick={() => { setFilterRole('all'); setFilterStatus('active'); setFilterAdmin('all'); }}
                className="text-xs text-red-500 hover:text-red-700">Clear all</button>
            </>
          )}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/60">
              <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider pl-4 pr-2 py-2.5">User</th>
              <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider px-2 py-2.5">Role</th>
              <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider px-2 py-2.5">Access</th>
              <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider px-2 py-2.5">Status</th>
              <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider px-2 py-2.5">Last Login</th>
              <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider px-2 py-2.5">Joined</th>
              <th className="text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider pl-2 pr-4 py-2.5 w-20"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {paginated.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-sm text-gray-400">No users found</td></tr>
            )}
            {paginated.map((u) => (
              <tr key={u.id} className={`hover:bg-gray-50/50 transition-colors ${!u.is_active ? 'opacity-50' : ''}`}>
                {/* User */}
                <td className="pl-4 pr-2 py-2">
                  <div className="flex items-center gap-2.5">
                    <div className={`h-7 w-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0 ${u.is_active ? 'bg-gradient-to-br from-blue-500 to-blue-600' : 'bg-gray-300'}`}>
                      {getInitials(u)}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {u.first_name} {u.last_name}
                        {u.id === currentUser?.id && <span className="ml-1 text-[9px] font-semibold text-blue-600 bg-blue-50 px-1 py-px rounded">you</span>}
                      </div>
                      <div className="text-[11px] text-gray-400 truncate">{u.email}</div>
                    </div>
                  </div>
                </td>

                {/* Role */}
                <td className="px-2 py-2">
                  <div className="relative">
                    <button
                      onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setRoleDropdown(roleDropdown?.id === u.id ? null : { id: u.id, top: rect.bottom + window.scrollY, left: rect.left });
                      }}
                      className="flex items-center gap-1 text-xs px-2 py-0.5 rounded border border-transparent hover:border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      {u.role_name ? (
                        <>
                          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: u.role_color || '#6366F1' }} />
                          <span className="text-gray-700">{u.role_name}</span>
                        </>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                      <ChevronDown className="h-2.5 w-2.5 text-gray-300 ml-0.5" />
                    </button>
                    {roleDropdown?.id === u.id && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setRoleDropdown(null)} />
                        <div className="fixed z-50 w-52 bg-white rounded-lg shadow-lg border border-gray-200 py-0.5 max-h-52 overflow-auto"
                          style={{ top: roleDropdown?.top || 0, left: roleDropdown?.left || 0 }}>
                          <button onClick={() => handleRoleChange(u.id, null)}
                            className="w-full text-left px-2.5 py-1.5 text-xs text-gray-400 hover:bg-gray-50 italic">No role</button>
                          {roles.filter(r => r.is_active).map((role) => (
                            <button key={role.id} onClick={() => handleRoleChange(u.id, role.id)}
                              className={`w-full text-left px-2.5 py-1.5 text-xs hover:bg-gray-50 flex items-center gap-1.5 ${u.role_id === role.id ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                            >
                              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: role.color || '#6366F1' }} />
                              {role.name}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </td>

                {/* Access */}
                <td className="px-2 py-2">
                  {u.is_admin ? (
                    <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-px rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                      <ShieldCheck className="h-2.5 w-2.5" /> Admin
                    </span>
                  ) : (
                    <span className="text-[11px] text-gray-400">Member</span>
                  )}
                </td>

                {/* Status */}
                <td className="px-2 py-2">
                  {u.is_active ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-px rounded-full bg-green-50 text-green-700">
                      <span className="w-1 h-1 rounded-full bg-green-500" /> Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-px rounded-full bg-gray-100 text-gray-500">
                      <span className="w-1 h-1 rounded-full bg-gray-400" /> Inactive
                    </span>
                  )}
                </td>

                {/* Last Login */}
                <td className="px-2 py-2">
                  <span className="text-[11px] text-gray-500">{formatDate(u.last_login_at)}</span>
                </td>

                {/* Joined */}
                <td className="px-2 py-2">
                  <span className="text-[11px] text-gray-400">{formatDate(u.created_at)}</span>
                </td>

                {/* Actions */}
                <td className="pl-2 pr-4 py-2 text-right">
                  {u.id !== currentUser?.id && (
                    <div className="flex items-center justify-end gap-0.5">
                      <button onClick={() => handleToggleAdmin(u)} title={u.is_admin ? 'Remove admin' : 'Make admin'}
                        className={`p-1 rounded text-gray-300 transition-colors ${u.is_admin ? 'hover:text-amber-600 hover:bg-amber-50' : 'hover:text-amber-500 hover:bg-amber-50'}`}>
                        <Shield className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => handleToggleActive(u)} title={u.is_active ? 'Deactivate' : 'Reactivate'}
                        className={`p-1 rounded transition-colors ${u.is_active ? 'text-gray-300 hover:text-red-500 hover:bg-red-50' : 'text-gray-300 hover:text-green-500 hover:bg-green-50'}`}>
                        {u.is_active ? <UserX className="h-3.5 w-3.5" /> : <UserCheck className="h-3.5 w-3.5" />}
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        {filtered.length > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100 bg-gray-50/40">
            <span className="text-[11px] text-gray-400">
              {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed">
                <ChevronLeft className="h-3.5 w-3.5 text-gray-600" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} onClick={() => setPage(p)}
                  className={`min-w-[24px] h-6 text-[11px] rounded ${p === page ? 'bg-gray-800 text-white font-semibold' : 'text-gray-500 hover:bg-gray-200'}`}
                >{p}</button>
              ))}
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed">
                <ChevronRight className="h-3.5 w-3.5 text-gray-600" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Summary footer */}
      <div className="flex items-center gap-4 mt-3 px-1">
        <span className="text-[11px] text-gray-400">{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
        <span className="text-[11px] text-gray-300">·</span>
        <span className="text-[11px] text-gray-400">{users.filter(u => u.is_admin).length} admin{users.filter(u => u.is_admin).length !== 1 ? 's' : ''}</span>
        <span className="text-[11px] text-gray-300">·</span>
        <span className="text-[11px] text-gray-400">{users.filter(u => !u.role_id && u.is_active).length} unassigned</span>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
          onClick={() => setShowInviteModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 overflow-hidden shadow-xl"
            onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-base font-semibold text-gray-900">Invite a Worker</h3>
              <p className="text-sm text-gray-500 mt-0.5">They'll receive an email to set up their account</p>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Email address *</label>
                <input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleInvite()}
                  placeholder="worker@example.com"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500"
                  autoFocus />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Role (optional)</label>
                <select value={inviteRoleId} onChange={e => setInviteRoleId(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500 bg-white">
                  <option value="">No role assigned</option>
                  {roles.map(r => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
              <button onClick={() => setShowInviteModal(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                Cancel
              </button>
              <button onClick={handleInvite} disabled={!inviteEmail.trim() || inviting}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg disabled:opacity-50 transition-colors">
                {inviting ? 'Sending...' : 'Send Invite'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
